import React from 'react';

const TransferOrder: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Transfer Orders</h1>
          <p className="text-gray-600 mt-2">
            The Transfer Orders page in DAS allows you to manage warehouse transfers efficiently. Track and manage the movement of products between different warehouse locations, monitor transfer status, and maintain accurate inventory records across all your locations.
          </p>
        </div>

        {/* Main Transfer Orders List Image */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="mb-6">
            <img
              src="/images/transfer-order/list.png"
              alt="Transfer Orders List Page"
              className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm"
            />
          </div>
          <p className="text-gray-600 text-sm">
            <strong>Example Transfer Orders List:</strong> The main interface showing all transfer orders with their status, dates, quantities, and warehouse information. You can create new transfers, view details, and track progress from this page.
          </p>
        </div>

        {/* Transfer Orders Overview Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Transfer Orders Overview</h2>
          <p className="text-gray-600 mb-4">
            The Transfer Orders page provides a comprehensive view of all warehouse transfers with a table format that displays essential transfer information. You can track transfer status, quantities, and manage transfers between different warehouse locations.
          </p>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Transfer Orders Table Columns</h3>
          <p className="text-gray-600 mb-4">The transfer orders table displays the following information for each transfer:</p>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
            <li><strong>Date:</strong> The date when the transfer order was created</li>
            <li><strong>Transfer Order #:</strong> Unique identifier for the transfer (e.g., TO-250800001)</li>
            <li><strong>Reason:</strong> The reason for the transfer (such as stock redistribution, demand fulfillment)</li>
            <li><strong>Status:</strong> Current status of the transfer (Confirmed, Pending, In Transit, Completed)</li>
            <li><strong>Quantity Transferred:</strong> Total number of items being transferred</li>
            <li><strong>Source Warehouse:</strong> The warehouse from which products are being transferred</li>
            <li><strong>Destination Warehouse:</strong> The warehouse to which products are being transferred</li>
          </ul>
          <p className="text-gray-600">The table provides a clear overview of all transfer activities, making it easy to track inventory movements across locations.</p>
        </div>

        {/* Creating New Transfer Orders Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Creating New Transfer Orders</h2>
          <p className="text-gray-600 mb-4">
            Creating transfer orders is straightforward using the <strong>+ New</strong> button in the Transfer Orders page.
          </p>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Transfer Order Creation Process</h3>
          <p className="text-gray-600 mb-4">To create a new transfer order:</p>
          <ol className="list-decimal list-inside text-gray-600 space-y-2 mb-4">
            <li>Click the <strong>+ New</strong> button in the top-right corner</li>
            <li>The "New Transfer Order" form will open with the following sections:</li>
          </ol>

          <div className="mb-6">
            <img
              src="/images/transfer-order/form.png"
              alt="New Transfer Order Creation Form"
              className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm"
            />
            <p className="text-gray-600 text-sm mt-2">
              <strong>New Transfer Order Form:</strong> The form interface for creating new transfer orders with date selection, reason, warehouse selection, and product details.
            </p>
          </div>

          <h4 className="text-lg font-semibold text-gray-800 mb-3">Basic Transfer Information</h4>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
            <li><strong>Date:</strong> Select the transfer order date (defaults to current date)</li>
            <li><strong>Reason:</strong> Choose the reason for the transfer from predefined options</li>
            <li><strong>Source Warehouse:</strong> Select the warehouse from which products will be transferred</li>
            <li><strong>Destination Warehouse:</strong> Select the warehouse to which products will be transferred</li>
          </ul>

          <h4 className="text-lg font-semibold text-gray-800 mb-3">Product Selection</h4>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
            <li><strong>Product Details:</strong> Search and select products to transfer</li>
            <li><strong>Batch Number:</strong> Select specific batches if batch tracking is enabled</li>
            <li><strong>Current Availability:</strong> View current stock levels in source and destination warehouses</li>
            <li><strong>Transfer Quantity:</strong> Specify the quantity to transfer</li>
          </ul>

          <h4 className="text-lg font-semibold text-gray-800 mb-3">Additional Options</h4>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
            <li><strong>Add New Row:</strong> Add multiple products to the same transfer order</li>
            <li><strong>Add Products in Bulk:</strong> Import multiple products at once</li>
            <li><strong>Attachments:</strong> Upload supporting documents (maximum 5 files, 5MB each)</li>
          </ul>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-green-800">
              <strong>Tip:</strong> The system automatically shows current stock availability to prevent over-transferring from the source warehouse.
            </p>
          </div>

          <p className="text-gray-600 mb-4">After filling all required information:</p>
          <ol className="list-decimal list-inside text-gray-600 space-y-2">
            <li>Review all transfer details for accuracy</li>
            <li>Click <strong>Save</strong> to create the transfer order</li>
            <li>The transfer order will appear in your transfer orders list</li>
            <li>It will be available for processing and tracking</li>
          </ol>
        </div>

        {/* Transfer Order Details Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Transfer Order Details</h2>
          <p className="text-gray-600 mb-4">
            Once transfer orders are created, you can view detailed information and track their progress.
          </p>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Viewing Transfer Details</h3>
          <p className="text-gray-600 mb-4">To access detailed transfer information:</p>
          <ol className="list-decimal list-inside text-gray-600 space-y-2 mb-4">
            <li>From the Transfer Orders list, click on any transfer row to open the transfer details</li>
            <li>The transfer detail page will open showing comprehensive information:</li>
          </ol>

          <div className="mb-6">
            <img
              src="/images/transfer-order/detail.png"
              alt="Transfer Order Detail Page"
              className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm"
            />
            <p className="text-gray-600 text-sm mt-2">
              <strong>Transfer Order Details:</strong> Comprehensive view showing transfer information, product details, journal entries, and document attachments with PDF generation capability.
            </p>
          </div>

          <h4 className="text-lg font-semibold text-gray-800 mb-3">Transfer Detail Information</h4>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-gray-600 mb-2"><strong>Transfer Header:</strong></p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                <li>Transfer Order Number</li>
                <li>Transfer Date</li>
                <li>Transfer Reason</li>
                <li>Current Status</li>
                <li>Source Warehouse</li>
                <li>Destination Warehouse</li>
              </ul>
            </div>
            <div>
              <p className="text-gray-600 mb-2"><strong>Product Information:</strong></p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                <li>Product names and descriptions</li>
                <li>Quantities being transferred</li>
                <li>Batch numbers (if applicable)</li>
                <li>Unit of measurement</li>
                <li>Transfer status per product</li>
              </ul>
            </div>
          </div>

          <h4 className="text-lg font-semibold text-gray-800 mb-3">Journal Entries</h4>
          <p className="text-gray-600 mb-4">
            The system automatically generates accounting journal entries for transfer orders, showing the movement of inventory assets between warehouses:
          </p>
        </div>

        {/* PDF and Documentation Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Transfer Order Documentation</h2>
          <p className="text-gray-600 mb-4">
            DAS provides comprehensive documentation features for transfer orders:
          </p>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">PDF Generation</h3>
          <p className="text-gray-600 mb-4">Generate professional PDF documents for transfer orders:</p>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
            <li><strong>Transfer Order PDF:</strong> Complete transfer documentation with all product details</li>
            <li><strong>Print Option:</strong> Direct printing functionality for physical documentation</li>
            <li><strong>Email Integration:</strong> Send transfer documents via email to relevant stakeholders</li>
          </ul>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">File Attachments</h3>
          <p className="text-gray-600 mb-4">Attach supporting documents to transfer orders:</p>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
            <li>Upload supporting documents (invoices, delivery notes, etc.)</li>
            <li>Maximum 5 files per transfer order</li>
            <li>5MB maximum file size per attachment</li>
            <li>Supports common file formats (PDF, images, documents)</li>
          </ul>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Documentation Benefits:</strong> Proper documentation ensures audit compliance, facilitates communication between warehouses, and provides clear records for inventory management.
            </p>
          </div>
        </div>

        {/* Inventory Impact Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Inventory Impact</h2>
          <p className="text-gray-600 mb-4">
            Understanding how transfer orders affect inventory levels across warehouses:
          </p>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Stock Level Changes</h3>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
            <li><strong>Source Warehouse:</strong> Stock levels decrease when transfer is confirmed</li>
            <li><strong>Destination Warehouse:</strong> Stock levels increase when transfer is completed</li>
          </ul>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Real-time Updates</h3>
          <p className="text-gray-600 mb-4">The system provides real-time inventory updates:</p>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
            <li>Immediate stock reduction at source warehouse upon confirmation</li>
            <li>Real-time availability checking prevents over-transfers</li>
            <li>Automatic stock increase at destination upon completion</li>
            <li>Integration with other inventory transactions</li>
          </ul>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              <strong>Important:</strong> Ensure transfer orders are properly completed to maintain accurate inventory levels. Incomplete transfers may result in discrepancies between physical and system stock.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransferOrder;
